// React, Next
import { ReactElement, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import Image from 'next/image'
import Link from 'next/link'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Icons
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

// Custom
import Section from '@/components/sections/Section'
import { translateHref } from '@/assets/functions/links'
import { GlobalContext } from 'context/GlobalContextProvider'

// Img
import CtaImage from 'public/img/services/consulting.webp'

// Config
import nextI18nextConfig from 'next-i18next.config'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody, getFontBodySmall, getFontH2 } from '@/assets/theme/fonts'

/** Tint behind the two small benefit icons. */
const BLUE_TINT = 'rgba(90, 109, 205, 0.12)'

export default function CtaSection() {
  const { t } = useTranslation('servicesOtCybersecurity')
  const { screenDimensions } = useContext(GlobalContext)

  const router = useRouter()
  const currentLocale =
    router.query.locale || nextI18nextConfig.i18n.defaultLocale

  const w = screenDimensions.width
  const stacked = w < breakpoints.desktopToMobile

  const benefits: { icon: ReactElement; title: string; text: string }[] = [
    {
      icon: (
        <CalendarMonthOutlinedIcon
          sx={{ fontSize: '1.4rem', color: themeColors.bg.start }}
        />
      ),
      title: t('getStarted.benefits.01.title'),
      text: t('getStarted.benefits.01.text')
    },
    {
      icon: (
        <ShieldOutlinedIcon
          sx={{ fontSize: '1.4rem', color: themeColors.bg.start }}
        />
      ),
      title: t('getStarted.benefits.02.title'),
      text: t('getStarted.benefits.02.text')
    }
  ]

  const getBenefit = (benefit: {
    icon: ReactElement
    title: string
    text: string
  }) => (
    <Box
      key={benefit.title}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: '0.85rem' }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: '2.5rem',
          height: '2.5rem',
          borderRadius: '8px',
          background: BLUE_TINT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {benefit.icon}
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: getFontBodySmall(),
            fontWeight: 700,
            color: themeColors.kroemtech.dunkelblau,
            marginBottom: '0.2rem'
          }}
        >
          {benefit.title}
        </Typography>
        <Typography
          sx={{
            fontSize: getFontBodySmall(),
            lineHeight: 1.5,
            color: themeColors.gray.defaultText
          }}
        >
          {benefit.text}
        </Typography>
      </Box>
    </Box>
  )

  /** The blue action card - overlaps the photo on wide screens. */
  const getActionCard = () => (
    <Box
      sx={
        stacked
          ? { width: '100%' }
          : {
              position: 'absolute',
              right: '2rem',
              bottom: '2rem',
              width: '17rem',
              zIndex: 2
            }
      }
    >
      <Link href={translateHref(currentLocale, 'contact')}>
        <Box
          sx={{
            background: themeColors.bg.start,
            borderRadius: stacked ? 0 : '10px',
            padding: '1.5rem',
            cursor: 'pointer',
            transition: 'background 0.2s',
            '&:hover': { background: themeColors.bg.light }
          }}
        >
          <Box
            sx={{
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1rem'
            }}
          >
            <CalendarMonthOutlinedIcon
              sx={{ fontSize: '1.4rem', color: '#fff' }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: '0.75rem'
            }}
          >
            <Typography
              sx={{
                fontSize: '1.05rem',
                fontWeight: 700,
                lineHeight: 1.3,
                color: '#fff'
              }}
            >
              {t('getStarted.cta')}
            </Typography>
            <ArrowForwardIcon
              sx={{ flexShrink: 0, fontSize: '1.3rem', color: '#fff' }}
            />
          </Box>

          <Typography
            sx={{
              fontSize: getFontBodySmall(),
              lineHeight: 1.5,
              color: 'rgba(255,255,255,0.8)',
              marginTop: '1rem'
            }}
          >
            {t('getStarted.note')}
          </Typography>
        </Box>
      </Link>
    </Box>
  )

  const getContent = () => (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: stacked ? 'column' : 'row',
        alignItems: 'stretch',
        width: '100%',
        background: themeColors.kroemtech.background,
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '10px',
        overflow: 'hidden'
      }}
    >
      {/* Copy */}
      <Box
        sx={{
          flex: stacked ? 'none' : '0 0 52%',
          padding: stacked ? '2rem 1.5rem' : '3rem'
        }}
      >
        <Typography
          variant="h2"
          component="h2"
          fontSize={getFontH2()}
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            color: themeColors.kroemtech.dunkelblau,
            maxWidth: '22rem',
            marginBottom: '1.25rem'
          }}
        >
          {t('getStarted.title')}
        </Typography>

        <Typography
          sx={{
            fontSize: getFontBody(),
            lineHeight: 1.7,
            color: themeColors.gray.defaultText,
            maxWidth: '26rem',
            marginBottom: '2rem'
          }}
        >
          {t('getStarted.description')}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns:
              w > breakpoints.mobileMid ? 'repeat(2, 1fr)' : '1fr',
            gap: '1.5rem'
          }}
        >
          {benefits.map(benefit => getBenefit(benefit))}
        </Box>
      </Box>

      {/* Photo */}
      <Box
        sx={{
          flex: stacked ? 'none' : '1 1 auto',
          position: 'relative',
          minHeight: stacked ? '14rem' : '22rem'
        }}
      >
        <Image
          src={CtaImage}
          alt=""
          fill
          sizes="(max-width: 990px) 100vw, 48vw"
          style={{ objectFit: 'cover', objectPosition: 'center' }}
        />

        {/* Fades the photo into the copy panel so the two halves meet softly.
            Stacked, the panel sits above the photo, so the fade runs downwards. */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            background: stacked
              ? `linear-gradient(to bottom, ${themeColors.kroemtech.background} 0%, rgba(255,255,255,0.6) 12%, rgba(255,255,255,0) 38%)`
              : `linear-gradient(to right, ${themeColors.kroemtech.background} 0%, rgba(255,255,255,0.75) 14%, rgba(255,255,255,0) 42%)`
          }}
        />
      </Box>

      {getActionCard()}
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.section.light}
      paddingTop="4rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
